<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      ref="overlayRef"
      class="searchOverlay"
      data-test="search-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('search.title')"
      @keydown.esc="close"
      @keydown.tab="onTab"
    >
      <div class="searchOverlay_row">
        <!-- Без clearable: внутри поля остаётся индикационная лупа. Крестик
             очистки дублировал бы крестик закрытия оверлея справа. -->
        <ac-search-input
          v-model="draft"
          autofocus
          @submit="onSubmit"
        />
        <button
          class="searchOverlay_close"
          data-test="search-overlay-close"
          type="button"
          :aria-label="$t('search.close')"
          @click="close"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 5L5 19M5 5L19 19" stroke="#697076" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import AcSearchInput from '@web/ui-kit/components/AcSearchInput.vue'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; submit: [query: string] }>()

const draft = ref('')
const overlayRef = ref<HTMLElement | null>(null)

// Закрытие без сабмита стирает введённый текст (требование эпика SEARCH-HOME).
// Здесь же — блокировка/разблокировка скролла body по проектному паттерну (см. headers/mobile.vue).
watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) draft.value = ''
  document.body.style = isOpen ? 'overflow: hidden; position: fixed; width: 100%;' : ''
})

// Если компонент размонтируют, пока оверлей открыт, — не оставляем body заблокированным навсегда.
onBeforeUnmount(() => {
  if (props.modelValue) document.body.style = ''
})

function close (): void {
  emit('update:modelValue', false)
}

function onSubmit (query: string): void {
  emit('submit', query)
  close()
}

// Простая фокус-ловушка: Tab/Shift+Tab не выпускают фокус за пределы оверлея.
function onTab (event: KeyboardEvent): void {
  const container = overlayRef.value
  if (!container) return

  const focusable = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )
  if (focusable.length === 0) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (event.shiftKey) {
    if (active === first || !container.contains(active)) {
      event.preventDefault()
      last.focus()
    }
  } else {
    if (active === last) {
      event.preventDefault()
      first.focus()
    }
  }
}
</script>

<style lang="scss" scoped>
.searchOverlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  // 1000 совпадал с navBarMobile (см. platform.v3/components/menu/navBarMobile.vue) —
  // перекрытие держалось только на порядке teleport в конец body, а не на
  // самом z-index. 10000 — значение полноэкранных оверлеев в проекте
  // (см. BottomSheetNewApp.vue), гарантированно выше navBarMobile.
  z-index: 10000;
  padding: 16px;
  background: var(--ac-color-white);

  &_row {
    display: flex;
    align-items: center;
  }

  &_close {
    flex-shrink: 0;
    border: none;
    background: none;
    color: var(--ac-color-black);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 12px;
  }
}
</style>
